import { isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

export interface EzremsRoomBindingScope {
  property_id: string;
  room_type_id: string;
  room_topology_revision: number;
  property_version: number;
  type_version: number;
}

export interface EzremsRoomBindingCommand {
  command_id: string;
  expected_room_version: number;
  /** 0 = 최초 연결, 1 이상 = 현재 연결의 재설정(서버가 previous를 파생한다). */
  expected_binding_revision: number;
  scope: EzremsRoomBindingScope;
  branch_id: number;
  branch_code: string;
  project_code: string;
  resource_id: number;
  dong: string;
  floor: string;
  ho2: string;
  reason: string;
}

export interface EzremsRoomBindingReceipt {
  command_id: string;
  room_id: string;
  room_version: number;
  binding_revision: number;
  http_status: 200;
}

type RecordValue = Record<string, unknown>;
const record = (value: unknown): value is RecordValue =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const exactKeys = (value: RecordValue, keys: readonly string[]) =>
  Object.keys(value).length === keys.length && keys.every((key) => Object.hasOwn(value, key));
// JSON numbers outside the safe range cannot carry the server's strict int64 CAS exactly.
const positiveInteger = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
const nonNegativeInteger = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
const coordinate = (value: unknown): value is string =>
  typeof value === 'string' &&
  Array.from(value).length >= 1 &&
  Array.from(value).length <= 128 &&
  value === value.trim();
const scopeRevisions = ['room_topology_revision', 'property_version', 'type_version'] as const;
const commandKeys = [
  'command_id',
  'expected_room_version',
  'expected_binding_revision',
  'scope',
  'branch_id',
  'branch_code',
  'project_code',
  'resource_id',
  'dong',
  'floor',
  'ho2',
  'reason',
] as const;
const receiptKeys = [
  'command_id',
  'room_id',
  'room_version',
  'binding_revision',
  'http_status',
] as const;

function invalidResponse(status?: number): ApiError {
  return new ApiError({
    kind: 'invalid-response',
    service: 'core',
    status,
    message: 'EZREMS 객실 연결 요청 또는 응답을 확인하지 못했습니다.',
  });
}

export function validateEzremsBindingCommand(roomId: unknown, command: unknown): void {
  const scope: unknown = record(command) ? command.scope : undefined;
  if (
    !isUuid(roomId) ||
    !record(command) ||
    !exactKeys(command, commandKeys) ||
    !isUuid(command.command_id) ||
    !positiveInteger(command.expected_room_version) ||
    !nonNegativeInteger(command.expected_binding_revision) ||
    !record(scope) ||
    !exactKeys(scope, ['property_id', 'room_type_id', ...scopeRevisions]) ||
    !isUuid(scope.property_id) ||
    !isUuid(scope.room_type_id) ||
    scopeRevisions.some((key) => !positiveInteger(scope[key])) ||
    !positiveInteger(command.branch_id) ||
    !positiveInteger(command.resource_id) ||
    ![command.branch_code, command.project_code, command.dong, command.floor, command.ho2].every(
      coordinate,
    ) ||
    typeof command.reason !== 'string' ||
    command.reason.trim().length === 0 ||
    Array.from(command.reason).length > 500
  )
    throw invalidResponse();
}

export function parseEzremsBindingReceipt(
  value: unknown,
  roomId: string,
  command: EzremsRoomBindingCommand,
): EzremsRoomBindingReceipt {
  if (
    !record(value) ||
    !exactKeys(value, receiptKeys) ||
    !isUuid(value.command_id) ||
    value.command_id.toLowerCase() !== command.command_id.toLowerCase() ||
    !isUuid(value.room_id) ||
    value.room_id.toLowerCase() !== roomId.toLowerCase() ||
    !positiveInteger(value.room_version) ||
    value.room_version !== command.expected_room_version + 1 ||
    value.binding_revision !== command.expected_binding_revision + 1 ||
    value.http_status !== 200
  )
    throw invalidResponse(200);
  return value as unknown as EzremsRoomBindingReceipt;
}

/** One explicit command only. A failed/uncertain response must never trigger automatic replay. */
export async function bindEzremsRoom(
  client: ServiceClient,
  roomId: string,
  command: EzremsRoomBindingCommand,
): Promise<EzremsRoomBindingReceipt> {
  validateEzremsBindingCommand(roomId, command);
  const value = await client.post<unknown>(`admin/rooms/${roomId}/ezrems-binding`, {
    json: command,
  });
  return parseEzremsBindingReceipt(value, roomId, command);
}
